import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useState, useEffect, useContext, useRef } from 'react'
import { Breadcrumbs, Button, LinearProgress, ListItemButton, Typography } from '@mui/material'

import '../styles/common-documents.css'
import { countriesCurrencies } from '../lib'
import { useSocket } from '../hooks/use-socket'
import HelpIcon from '../assets/icons/help-icon.svg'
import { Document, SystemUser, UserSessionType } from '../types'
import Dialog from '../components/common/dialog'
import { normalizeDocumentData, upsertEntityWithLimitAtStart, upsertManyWithLimitAtStartFast } from '../utils'
import { FormError } from '../types/common.types'
import { requestAccessToken } from '../api/auth.api'
import NextIcon from '../assets/icons/next-icon.svg'
import { AuthContext } from '../contexts/auth.context'
import { UserContext } from '../contexts/user.context'
import Pagination from '../components/common/pagination'
import { getBatchesToReviewData } from '../api/app-data.api'
import { AppStateContext } from '../contexts/app-state.context'
import ActionsBar from '../components/common/documents-actions-bar'
import { NotificationsContext } from '../contexts/notifications.context'
import {deleteDocumentData, updateDocumentData, resolveDocumentsAccessToken } from '../api/documents.api'
import { FiltersOptionsModal } from '../components/common/filters-options-modal'
import { ProcessingDocumentWarning } from '../components/common/processing-document-warning'
import DocumentsToReviewListItem from '../components/documents-to-review-management/documents-to-review-list-item'
import { handleUpdateDataByOne, handleDeleteDataByOne, handleGetDataFromTableById } from '../utils/db/common-methods'
import { baseButtonBlueBg } from '../theme/custom-styles'
import { searchBatchesByRegex } from '../api/app-queries.api'

const DocumentsToReviewManagement = () => {
  const batchesToReviewStatus = ['stopped', 'uploaded', 'downloaded', 'analysis-completed', 'downloaded-n-analyzing']
  const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
  const currency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toLowerCase()

  const navigate = useNavigate()
  const { userSession } = useContext(UserContext)
  const { isAuth, removeAuth } = useContext(AuthContext)
  const { appDataSocket, isAppDataSocketInitialized } = useSocket()
  const { setScrollToTop, processingBatch } = useContext(AppStateContext)
  const { updateNewNotificationStatus } = useContext(NotificationsContext)
  const { t } = useTranslation(['documents-to-review-management', 'common'])

  const currentListPageRef = useRef<number>(1)
  const currentLoadingDataRef = useRef<boolean>(true)
  const documentToSearchRef = useRef<string | null>(null)
  const currentDocumentsDataRef = useRef<Array<Document>>([])
  const noRegistriesFoundAtDatabaseRef = useRef<boolean>(false)
  const documentsToDeleteRef = useRef<Array<string> | null>(null)
  const currentShowDeleteButtonFromActionsBarRef = useRef<boolean | null>(null)

  const [errors, setErrors] = useState<FormError>({})
  const [pageSize, setPageSize] = useState<number>(50)
  const [currentListPage, setCurrentListPage] = useState<number>(1)
  const [documentToSearch, setDocumentToSearch] = useState<string>('')
  const [loadingLocalData, setLoadingLocalData] = useState<boolean>(true)
  const [documentsData, setDocumentsData] = useState<Array<Document>>([])
  const [documentsList, setDocumentsList] = useState<Array<Document>>([])
  const [searchingInDatabase, setSearchingInDatabase] = useState<boolean>(false)
  const [selectAllDocuments, setSelectAllDocuments] = useState<boolean>(false)
  const [systemUsersData, setSystemUsersData] = useState<Array<SystemUser>>([])
  const [documentsToDelete, setDocumentsToDelete] = useState<Array<string>>([])
  const [selectedDocument, setSelectedDocument] = useState<Partial<Document>>({})
  const [searchedDocuments, setSearchedDocuments] = useState<Array<Document>>([])
  const [openStopDocumentModal, setOpenStopDocumentModal] = useState<boolean>(false)
  const [openFiltersOptionsModal, setOpenFiltersOptionsModal] = useState<boolean>(false)
  const [openResumeDocumentModal, setOpenResumeDocumentModal] = useState<boolean>(false)
  const [openDeleteDocumentModal, setOpenDeleteDocumentModal] = useState<boolean>(false)
  const [noRegistriesFoundAtLocal, setNoRegistriesFoundAtLocal] = useState<boolean>(false)
  const [dialogStopDocumentIsLoading, setDialogStopDocumentIsLoading] = useState<boolean>(false)
  const [noRegistriesFoundAtDatabase, setNoRegistriesFoundAtDatabase] = useState<boolean>(false)
  const [dialogResumeDocumentIsLoading, setDialogResumeDocumentIsLoading] = useState<boolean>(false)
  const [dialogDeleteDocumentIsLoading, setDialogDeleteDocumentIsLoading] = useState<boolean>(false)
  const [showDeleteButtonFromActionsBar, setShowDeleteButtonFromActionsBar] = useState<boolean>(false)

  useEffect(() => {
    if (!appDataSocket) return

    if (!userSession) return

    appDataSocket.on(`addNewBatchToReview:${selectedCountry}`, async (dataFromSocket: any) => {
      if (!batchesToReviewStatus.includes(dataFromSocket.status)) {
        const batchesToReviewDataUpdated = currentDocumentsDataRef.current.filter((documentData: Document) => String(documentData._id) !== String(dataFromSocket._id))
        globalThis.sessionStorage.setItem(`batchesToReviewData:${userSession?.belongsTo}:${selectedCountry}`, JSON.stringify({ batchesToReviewData: batchesToReviewDataUpdated }))

        handleSetInitialData({ batchesToReviewData: batchesToReviewDataUpdated })
        return
      }

      const batchesToReviewDataUpdated = upsertEntityWithLimitAtStart(currentDocumentsDataRef.current, dataFromSocket, 500)
      globalThis.sessionStorage.setItem(`batchesToReviewData:${userSession?.belongsTo}:${selectedCountry}`, JSON.stringify({ batchesToReviewData: batchesToReviewDataUpdated }))

      handleSetInitialData({ batchesToReviewData: batchesToReviewDataUpdated })
    })

    appDataSocket.on(`updateBatchToReview:${selectedCountry}`, async (dataFromSocket: any) => {
      if (!batchesToReviewStatus.includes(dataFromSocket.status) || (batchesToReviewStatus.includes(dataFromSocket.status) && dataFromSocket.deleted === true)) {
        const batchesToReviewDataUpdated = currentDocumentsDataRef.current.filter((documentData: Document) => String(documentData._id) !== String(dataFromSocket._id))
        globalThis.sessionStorage.setItem(`batchesToReviewData:${userSession?.belongsTo}:${selectedCountry}`, JSON.stringify({ batchesToReviewData: batchesToReviewDataUpdated }))

        handleSetInitialData({ batchesToReviewData: batchesToReviewDataUpdated })
        return
      }

      const batchesToReviewDataUpdated = upsertEntityWithLimitAtStart(currentDocumentsDataRef.current, dataFromSocket, 500)
      globalThis.sessionStorage.setItem(`batchesToReviewData:${userSession?.belongsTo}:${selectedCountry}`, JSON.stringify({ batchesToReviewData: batchesToReviewDataUpdated }))

      handleSetInitialData({ batchesToReviewData: batchesToReviewDataUpdated })
    })

    appDataSocket.on(`deleteBatchToReview:${selectedCountry}`, async (dataFromSocket: any) => {
      const batchesToReviewDataUpdated = currentDocumentsDataRef.current.filter((documentData: Document) => String(documentData._id) !== String(dataFromSocket.documentKey))
      globalThis.sessionStorage.setItem(`batchesToReviewData:${userSession?.belongsTo}:${selectedCountry}`, JSON.stringify({ batchesToReviewData: batchesToReviewDataUpdated }))

      handleSetInitialData({ batchesToReviewData: batchesToReviewDataUpdated })
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession, appDataSocket, isAppDataSocketInitialized])

  useEffect(() => {
    if (userSession) {
      if (!userSession.role.includes('-client')) {
        navigate('/404')
        return
      }
    }

    handleGetBatchesToReviewData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession])

  const handleGetBatchesToReviewData = async () => {
    let localBatchesToReviewDataToSet: any = {}
    const localBatchesToReviewData = globalThis.sessionStorage.getItem(`batchesToReviewData:${userSession?.belongsTo}:${selectedCountry}`)

    if (localBatchesToReviewData) {
      localBatchesToReviewDataToSet = JSON.parse(localBatchesToReviewData)
      handleSetInitialData(localBatchesToReviewDataToSet)
    }

    handleGetBatchesToReviewDataFromRemote()
  }

  const handleGetBatchesToReviewDataFromRemote = async () => {
    try {
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const { data } = await getBatchesToReviewData(selectedCountry, accessToken)

      handleSetInitialData(data)
      globalThis.sessionStorage.setItem(`batchesToReviewData:${userSession?.belongsTo}:${selectedCountry}`, JSON.stringify(data))
    } catch (error) {
      console.log('Error handleGetBatchesCompletedData > ', error)
    }
  }

  const handleSetInitialData = async (dataToSet: any) => {
    const currentSelectedDocumentsIds =
      currentDocumentsDataRef.current.length === 0 ? [] : currentDocumentsDataRef.current.filter((documentData) => documentData.selected === true).map((documentData) => String(documentData._id))
    const currentListPageDataState = currentListPageRef.current
    const currentDocumentToSearchState = documentToSearchRef.current
    const documentsToReviewNormalizedData = []

    for (const element of dataToSet.batchesToReviewData) {
      const documentData = { ...element }
      const documentNormalizedData = normalizeDocumentData(documentData) as Document
      // documentNormalizedData.uploadedByFullName = await handleGetUploadedByFullName(documentNormalizedData)

      if (currentSelectedDocumentsIds.includes(String(documentNormalizedData._id))) {
        documentNormalizedData.selected = true
      } else {
        documentNormalizedData.selected = false
      }

      documentsToReviewNormalizedData.push(documentNormalizedData)
    }

    documentsToReviewNormalizedData.sort((thisDocument: Partial<Document>, nextDocument: Partial<Document>) => {
      return Date.parse(nextDocument.createdAt) - Date.parse(thisDocument.createdAt)
    })

    if (currentDocumentToSearchState === '' || currentDocumentToSearchState === null) {
      const firstPageIndex = currentListPageDataState === null ? 0 : (currentListPageDataState - 1) * pageSize
      const lastPageIndex = firstPageIndex + pageSize
      const documentsPerPage = documentsToReviewNormalizedData.slice(firstPageIndex, lastPageIndex)

      handleDocumentsDeletionProcess(documentsPerPage)
      setDocumentsList(documentsPerPage)
    }

    currentDocumentsDataRef.current = documentsToReviewNormalizedData
    setSystemUsersData(dataToSet.systemUsers)
    setDocumentsData(documentsToReviewNormalizedData)

    if (currentLoadingDataRef.current === true) {
      currentLoadingDataRef.current = false
      setLoadingLocalData(false)
    }
  }

  useEffect(() => {
    if (documentsToDelete.length === 0) {
      currentShowDeleteButtonFromActionsBarRef.current = false
      setShowDeleteButtonFromActionsBar(false)
    } else {
      if (!selectedDocument.originalName) {
        currentShowDeleteButtonFromActionsBarRef.current = true
        setShowDeleteButtonFromActionsBar(true)
      }
    }
  }, [documentsToDelete, selectedDocument])

  useEffect(() => {
    const firstPageIndex = (currentListPage - 1) * pageSize
    const lastPageIndex = firstPageIndex + pageSize
    const documentsPerPage = documentToSearch === '' ? documentsData.slice(firstPageIndex, lastPageIndex) : searchedDocuments.slice(firstPageIndex, lastPageIndex)

    setDocumentsList(documentsPerPage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentListPage, documentsData, searchedDocuments])

  const handleDocumentsDeletionProcess = (documentsPerPage: Array<Document>) => {
    const currentDocumentsToDeleteState = (documentsToDeleteRef.current as Array<string>) ?? []
    const currentShowDeleteButtonFromActionsBarState = currentShowDeleteButtonFromActionsBarRef.current as boolean
    const documentsToDeleteOutOfPage = []

    for (const element of currentDocumentsToDeleteState) {
      const documentToDeleteId = element
      const findDocumentIndex = documentsPerPage?.findIndex((documentData) => String(documentData._id) === String(documentToDeleteId))

      if (findDocumentIndex === -1) {
        documentsToDeleteOutOfPage.push(documentToDeleteId)
      }
    }

    for (const element of documentsToDeleteOutOfPage) {
      const documentId = element
      const index = currentDocumentsToDeleteState.indexOf(documentId)
      if (index > -1) {
        currentDocumentsToDeleteState.splice(index, 1)
      }
    }

    documentsToDeleteRef.current = currentDocumentsToDeleteState
    if (documentsToDeleteRef.current.length < pageSize) {
      setSelectAllDocuments(false)
    }

    if (documentsToDeleteRef.current.length === 0) {
      setShowDeleteButtonFromActionsBar(false)
    } else {
      setShowDeleteButtonFromActionsBar(currentShowDeleteButtonFromActionsBarState)
    }

    setTimeout(() => {
      setDocumentsToDelete(currentDocumentsToDeleteState)
    }, 50)
  }

  const handleUncheckAllDocuments = () => {
    setSelectAllDocuments(false)
    setDocumentsData((currentDocumentsState) => {
      const documentsStateUpdated = [...currentDocumentsState]
      const documentsStateUpdatedToReturn = documentsStateUpdated.map((documentData) => ({
        ...documentData,
        selected: false
      }))

      currentDocumentsDataRef.current = documentsStateUpdatedToReturn
      return documentsStateUpdatedToReturn
    })
  }

  const handleSetDocumentToSearch = (documentToSearchData: string, showError: boolean) => {
    currentListPageRef.current = 1
    documentToSearchRef.current = documentToSearchData

    if (noRegistriesFoundAtDatabaseRef.current === true) {
      setNoRegistriesFoundAtDatabase(false)
    }

    setErrors({})
    setCurrentListPage(1)
    setNoRegistriesFoundAtLocal(false)
    setDocumentToSearch(documentToSearchData)

    if (documentToSearchData === '') {
      const firstPageIndex = (currentListPage - 1) * pageSize
      const lastPageIndex = firstPageIndex + pageSize
      const currentDocumentsDataState = currentDocumentsDataRef.current ?? []
      const documentsPerPage = currentDocumentsDataState.slice(firstPageIndex, lastPageIndex)

      setDocumentsList(documentsPerPage)
      setSearchedDocuments([])

      if (showError) setErrors({ documentToSearch: t('searchField.errors.emptyField') })
      return
    }

    const currentFullDocumentsData = [...(currentDocumentsDataRef.current || [])]
    const filteredDocuments = currentFullDocumentsData.filter((document) => {
      const documentSystemUserFullName = document.uploadedByFullName
      const documentStatus =
        document.status.indexOf('error') !== -1
          ? t('documentStatus.error')
          : document.status === 'dispersing' || document.status === 'register-disperse-completed'
          ? t('documentStatus.dispersing')
          : t('documentStatus.completed')

      if (document.selected === true) document.selected = false

      return (
        documentStatus.toLowerCase().includes(documentToSearchData.toLowerCase()) ||
        documentSystemUserFullName?.toLowerCase().includes(documentToSearchData.toLowerCase()) ||
        document.originalName.toLowerCase().includes(documentToSearchData.toLowerCase())
      )
    })

    documentsToDeleteRef.current = []
    const firstPageIndex = (currentListPage - 1) * pageSize
    const lastPageIndex = firstPageIndex + pageSize
    const documentsPerPage = filteredDocuments.slice(firstPageIndex, lastPageIndex)

    setDocumentsToDelete([])
    handleUncheckAllDocuments()
    setSelectAllDocuments(false)
    setDocumentsList(documentsPerPage)
    setSearchedDocuments(filteredDocuments)

    if (filteredDocuments.length === 0) {
      setNoRegistriesFoundAtLocal(true)
    }
  }

  const handleConfirmDocumentDeletion = async () => {
    setErrors({})
    setDialogDeleteDocumentIsLoading(true)
    const currentDocumentsToDelete = [...(documentsToDeleteRef.current as Array<string>)]

    if (selectedDocument.originalName && !documentsToDeleteRef.current?.includes(String(selectedDocument._id))) {
      documentsToDeleteRef.current = [String(selectedDocument._id)]
      setDocumentsToDelete([String(selectedDocument._id)])
      currentDocumentsToDelete.push(String(selectedDocument._id))
    }

    for (const element of currentDocumentsToDelete) {
      const documentToDeleteId = element

      if (processingBatch === true) {
        handleDiscardDeletionProcess()
        setOpenDeleteDocumentModal(false)
        updateNewNotificationStatus?.(true, 'deletion-process-interrupted')
        break
      }

      try {
        setDialogDeleteDocumentIsLoading(true)
        const accessToken = await resolveDocumentsAccessToken(isAuth?.replaceAll('"', '') ?? '')
        await deleteDocumentData(documentToDeleteId, accessToken)
        await handleDeleteDataByOne('documents', documentToDeleteId)
      } catch (error: any) {
        console.log(error, error?.response?.data?.message)
        if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
          handleCloseSession()
        } else if (error?.response?.status === 401) {
          setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
        } else {
          setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
        }

        handleDiscardDeletionProcess()
        return
      }
    }

    handleDiscardDeletionProcess()
    setOpenDeleteDocumentModal(false)
    updateNewNotificationStatus?.(true, 'document-deleted')
  }

  const handleDiscardDeletionProcess = () => {
    setDocumentsToDelete([])
    handleUncheckAllDocuments()
    documentsToDeleteRef.current = []
    setDialogDeleteDocumentIsLoading(false)
    const currentDocumentToSearchState = documentToSearchRef.current

    if (currentDocumentToSearchState !== '') {
      handleSetDocumentToSearch('', false)
    }
  }

  const handleConfirmDocumentStop = async () => {
    setErrors({})
    setDialogStopDocumentIsLoading(true)

    try {
      const selectedDocumentId = String(selectedDocument._id)
      const accessToken = await resolveDocumentsAccessToken(isAuth?.replaceAll('"', '') ?? '')
      await updateDocumentData(selectedDocumentId, { status: 'stopped' }, accessToken)

      const currentDocumentDataLocalData = (await handleGetDataFromTableById('documents', String(selectedDocument._id))) as Array<Document>

      if (
        currentDocumentDataLocalData &&
        currentDocumentDataLocalData.length > 0 &&
        currentDocumentDataLocalData[0].status !== 'analysis-completed' &&
        currentDocumentDataLocalData[0].status !== 'ready-for-disperse' &&
        currentDocumentDataLocalData[0].status !== 'dispersing' &&
        currentDocumentDataLocalData[0].status !== 'register-disperse-completed' &&
        currentDocumentDataLocalData[0].status !== 'completed'
      ) {
        const documentUpdated = { ...selectedDocument }
        documentUpdated.status = 'stopped'
        delete documentUpdated._id
        delete documentUpdated.selected
        delete documentUpdated.uploadedByFullName

        await handleUpdateDataByOne('documents', selectedDocumentId, { ...documentUpdated })
      }

      if (currentDocumentDataLocalData && currentDocumentDataLocalData.length > 0 && currentDocumentDataLocalData[0].status === 'ready-for-disperse') {
        navigate('/documents-ready')
        return
      }

      if (
        currentDocumentDataLocalData &&
        currentDocumentDataLocalData.length > 0 &&
        (currentDocumentDataLocalData[0].status === 'dispersing' || currentDocumentDataLocalData[0].status === 'register-disperse-completed' || currentDocumentDataLocalData[0].status === 'completed')
      ) {
        navigate('/documents-completed')
        return
      }

      const currentListPageDataState = currentListPageRef.current
      const currentDocumentsDataState = currentDocumentsDataRef.current
      const currentDocumentsDataUpdatedState = currentDocumentsDataState.map((documentData) => {
        if (String(documentData._id) === String(selectedDocument._id)) {
          documentData.status = 'stopped'
        }

        return documentData
      })
      currentDocumentsDataRef.current = currentDocumentsDataUpdatedState

      const firstPageIndex = currentListPageDataState === null ? 0 : (currentListPageDataState - 1) * pageSize
      const lastPageIndex = firstPageIndex + pageSize
      const documentsPerPage = currentDocumentsDataUpdatedState.slice(firstPageIndex, lastPageIndex)

      setOpenStopDocumentModal(false)
      setDialogStopDocumentIsLoading(false)
      updateNewNotificationStatus?.(true, 'document-stopped')

      if (documentToSearch !== '') {
        handleSetDocumentToSearch('', false)
      }

      setDocumentsList(documentsPerPage)
      setDocumentsData(currentDocumentsDataUpdatedState)
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setDialogStopDocumentIsLoading(false)
      return
    }
  }

  const handleConfirmDocumentResume = async () => {
    setErrors({})
    setDialogResumeDocumentIsLoading(true)

    try {
      const selectedDocumentId = String(selectedDocument._id)
      const accessToken = await resolveDocumentsAccessToken(isAuth?.replaceAll('"', '') ?? '')
      await updateDocumentData(selectedDocumentId, { status: 'downloaded' }, accessToken)

      const documentUpdated = { ...selectedDocument }
      documentUpdated.status = 'downloaded'
      delete documentUpdated.uploadedByFullName
      delete documentUpdated.selected
      delete documentUpdated._id

      const currentListPageDataState = currentListPageRef.current
      const currentDocumentsDataState = currentDocumentsDataRef.current
      const currentDocumentsDataUpdatedState = currentDocumentsDataState.map((documentData) => {
        if (String(documentData._id) === String(selectedDocument._id)) {
          documentData.status = 'downloaded'
        }

        return documentData
      })
      currentDocumentsDataRef.current = currentDocumentsDataUpdatedState

      await handleUpdateDataByOne('documents', selectedDocumentId, { ...documentUpdated })

      const firstPageIndex = currentListPageDataState === null ? 0 : (currentListPageDataState - 1) * pageSize
      const lastPageIndex = firstPageIndex + pageSize
      const documentsPerPage = currentDocumentsDataUpdatedState.slice(firstPageIndex, lastPageIndex)

      setOpenResumeDocumentModal(false)
      setDialogResumeDocumentIsLoading(false)
      updateNewNotificationStatus?.(true, 'document-resumed')

      if (documentToSearch !== '') {
        handleSetDocumentToSearch('', false)
      }

      setDocumentsList(documentsPerPage)
      setDocumentsData(currentDocumentsDataUpdatedState)
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setDialogResumeDocumentIsLoading(false)
      return
    }
  }

  const handleSearchBatchesToReviewDataByRegex = async () => {
    try {
      setErrors({})
      setDocumentsToDelete([])
      handleUncheckAllDocuments()
      setSearchingInDatabase(true)
      documentsToDeleteRef.current = []

      const {
        data: { accessToken }
      } = await requestAccessToken(String(isAuth).replaceAll('"', ''))

      const { data } = await searchBatchesByRegex({ searchText: documentToSearch, status: 'to-review', belongsTo: userSession?.belongsTo, currency }, accessToken)

      if (data.batchesToReviewData.length === 0) {
        setSearchingInDatabase(false)
        setNoRegistriesFoundAtDatabase(true)
        noRegistriesFoundAtDatabaseRef.current = true
        return
      }

      /* ESTO ES PARA ACTUALIZAR EL LISTADO SOLO CON LOS BUSCADOS */
      data.batchesToReviewData.sort((thisDocument: Partial<Document>, nextDocument: Partial<Document>) => {
        return Date.parse(nextDocument.createdAt) - Date.parse(thisDocument.createdAt)
      })
      setDocumentsList(data.batchesToReviewData)
      setSearchedDocuments(data.batchesToReviewData)
      /* ESTO ES PARA ACTUALIZAR EL LISTADO SOLO CON LOS BUSCADOS */

      const currentDocumentsData = [...currentDocumentsDataRef.current]
      const documentsDataUpdated = upsertManyWithLimitAtStartFast(currentDocumentsData, data.batchesToReviewData, 500)
      documentsDataUpdated.sort((thisDocument: Partial<Document>, nextDocument: Partial<Document>) => {
        return Date.parse(nextDocument.createdAt) - Date.parse(thisDocument.createdAt)
      })

      currentDocumentsDataRef.current = documentsDataUpdated
      setDocumentsData(documentsDataUpdated)

      setSearchingInDatabase(false)
      setNoRegistriesFoundAtLocal(false)
      globalThis.sessionStorage.setItem(`batchesToReviewData:${userSession?.belongsTo}:${selectedCountry}`, JSON.stringify({ batchesToReviewData: documentsDataUpdated }))
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setSearchingInDatabase(false)
      return
    }
  }

  const handleCloseSession = () => {
    if (removeAuth) {
      removeAuth()
      navigate('/', { replace: true, state: { sessionExpired: true } })
    }
  }

  const handleChangeStateToOpenModal = (selectedDocumentData: Document) => {
    const currentDocumentsList = [...documentsList]
    const documentsListUpdated = currentDocumentsList.map((documentData) => ({
      ...documentData,
      selected: false
    }))

    setSelectedDocument({
      ...selectedDocumentData
    })
    setSelectAllDocuments(false)
    setDocumentsList(documentsListUpdated)
    setShowDeleteButtonFromActionsBar(false)
  }

  return (
    <div className="section-container">
      <Breadcrumbs
        separator={
          <img
            src={NextIcon}
            alt="next-icon"
          />
        }
        aria-label="breadcrumb"
      >
        <Typography
          key="3"
          className="breadcrumb-inactive"
        >
          {t('breadcrumbs.home')}
        </Typography>
        <ListItemButton
          sx={{
            '&.MuiButtonBase-root': {
              padding: '0px'
            },
            '&.MuiButtonBase-root:hover': {
              backgroundColor: 'rgba(0,0,0,0)'
            }
          }}
          onClick={() => navigate(`/customer-company-batches/${userSession?.belongsTo}`)}
        >
          <Typography
            key="3"
            className="breadcrumb-inactive"
          >
            {t('breadcrumbs.firstLevel')}
          </Typography>
        </ListItemButton>
        <Typography
          key="3"
          className="breadcrumb-active"
        >
          {t('breadcrumbs.secondLevel')}
        </Typography>
      </Breadcrumbs>
      <ActionsBar
        errors={errors}
        searchingInDatabase={searchingInDatabase}
        documentToSearch={documentToSearch}
        processingDocument={processingBatch}
        generalActionChecked={selectAllDocuments}
        handleClearSearchInput={() => setErrors({})}
        showDeleteButton={showDeleteButtonFromActionsBar}
        handleSetDocumentToSearch={handleSetDocumentToSearch}
        handleOpenFiltersOptionsModal={() => setOpenFiltersOptionsModal(true)}
        documentsData={documentToSearch === '' ? documentsData : searchedDocuments}
        handleDialogOpenState={() => setOpenDeleteDocumentModal(!openDeleteDocumentModal)}
        handleToggleGeneralActionChecked={(checked) => {
          setSelectAllDocuments(checked)

          if (checked === true) {
            const currentDocumentsList = [...documentsList]
            const currentDocumentsDataStateUpdated = currentDocumentsDataRef.current
            const documentsListUpdated = currentDocumentsList.map((documentData) => ({
              ...documentData,
              selected: true
            }))
            const currentDocumentsDataIds = documentsListUpdated.map((documentData) => documentData._id)
            const filteredDocumentsToDelete = currentDocumentsDataIds.filter((id) => id !== undefined) as string[]
            currentShowDeleteButtonFromActionsBarRef.current = true
            documentsToDeleteRef.current = filteredDocumentsToDelete

            for (const element of documentsListUpdated) {
              const documentListData = element
              const findDocumentIndex = currentDocumentsDataStateUpdated?.findIndex((documentData) => String(documentData._id) === String(documentListData._id)) ?? -1

              if (findDocumentIndex !== -1 && currentDocumentsDataStateUpdated) {
                currentDocumentsDataStateUpdated[findDocumentIndex].selected = true
              }
            }

            currentDocumentsDataRef.current = currentDocumentsDataStateUpdated

            setDocumentsList(documentsListUpdated)
            setShowDeleteButtonFromActionsBar(true)
            setDocumentsToDelete(filteredDocumentsToDelete)
            setDocumentsData(currentDocumentsDataStateUpdated)
          } else {
            const currentDocumentsList = [...documentsList]
            const currentDocumentsDataStateUpdated = currentDocumentsDataRef.current
            const documentsListUpdated = currentDocumentsList.map((documentData) => ({
              ...documentData,
              selected: false
            }))

            for (const element of documentsListUpdated) {
              const documentListData = element
              const findDocumentIndex = currentDocumentsDataStateUpdated?.findIndex((documentData) => String(documentData._id) === String(documentListData._id)) ?? -1

              if (findDocumentIndex !== -1 && currentDocumentsDataStateUpdated) {
                currentDocumentsDataStateUpdated[findDocumentIndex].selected = false
              }
            }

            documentsToDeleteRef.current = []
            currentShowDeleteButtonFromActionsBarRef.current = false
            currentDocumentsDataRef.current = currentDocumentsDataStateUpdated

            handleUncheckAllDocuments()
            setDocumentsToDelete([])
            setDocumentsList(documentsListUpdated)
            setShowDeleteButtonFromActionsBar(false)
            setDocumentsData(currentDocumentsDataStateUpdated)
          }
        }}
      />
      {processingBatch && <ProcessingDocumentWarning />}
      <div className="table-header-container">
        <div className="documents-list-item-data-document-info table-header-text first-table-header">{t('tableHeaders.name')}</div>
        <div className="documents-list-item-uploaded-by-container table-header-text hide-lg-down">{t('tableHeaders.uploadedBy')}</div>
        <div className="documents-list-item-status-container table-header-text">{t('tableHeaders.status')}</div>
        <div className="documents-list-item-actions-container table-header-text last-table-header ">{t('tableHeaders.actions')}</div>
      </div>
      {loadingLocalData ? (
        <div className="documents-list-container">
          <div className="documents-list-item-data-container-loader"></div>
          <div className="documents-list-item-data-container-loader"></div>
          <div className="documents-list-item-data-container-loader"></div>
          <div className="documents-list-item-data-container-loader"></div>
          <div className="documents-list-item-data-container-loader"></div>
        </div>
      ) : (
        <>
          <div className="documents-list-container">
            {documentsList.map((documentData: Document) => {
              return (
                <DocumentsToReviewListItem
                  key={documentData._id}
                  userSession={userSession as UserSessionType}
                  documentData={documentData}
                  processingDocument={processingBatch || false}
                  handleGoToDocumentDetails={() => navigate(`/document-details/${documentData._id}`, { state: { previousPath: 'documents-to-review' } })}
                  handleOpenStopDocumentDataModal={(selectedDocumentData: Document) => {
                    setOpenStopDocumentModal(true)
                    handleChangeStateToOpenModal(selectedDocumentData)
                  }}
                  handleOpenResumeDocumentDataModal={(selectedDocumentData: Document) => {
                    setOpenResumeDocumentModal(true)
                    handleChangeStateToOpenModal(selectedDocumentData)
                  }}
                  handleToggleDocumentSelection={(selectedDocumentData: Document, selected: boolean) => {
                    let documentsSelected = 0
                    let documentsToDeleteUpdated = []
                    const currentDocumentsList = [...documentsList]
                    const currentDocumentsToDelete = [...documentsToDelete]
                    const currentDocumentsDataStateUpdated = currentDocumentsDataRef.current

                    const documentsListUpdated = currentDocumentsList.map((documentData) => {
                      if (String(documentData._id) === String(selectedDocumentData._id)) {
                        documentData.selected = selected
                      }

                      if (documentData.selected === true) documentsSelected++

                      return documentData
                    })

                    if (selected === true) {
                      documentsToDeleteUpdated = currentDocumentsToDelete.concat(selectedDocumentData._id ?? [])
                      documentsToDeleteRef.current = documentsToDeleteUpdated
                      setDocumentsToDelete(documentsToDeleteUpdated)

                      const findDocumentIndex = currentDocumentsDataStateUpdated?.findIndex((documentData) => String(documentData._id) === String(selectedDocumentData._id)) ?? -1

                      if (findDocumentIndex !== -1 && currentDocumentsDataStateUpdated) {
                        currentDocumentsDataStateUpdated[findDocumentIndex].selected = true
                      }

                      currentDocumentsDataRef.current = currentDocumentsDataStateUpdated
                      setDocumentsData(currentDocumentsDataStateUpdated)
                    } else {
                      const index = currentDocumentsToDelete.indexOf(selectedDocumentData._id ?? '')
                      if (index > -1) {
                        currentDocumentsToDelete.splice(index, 1)
                      }

                      documentsToDeleteUpdated = [...currentDocumentsToDelete]
                      documentsToDeleteRef.current = documentsToDeleteUpdated
                      setDocumentsToDelete(currentDocumentsToDelete)

                      const findDocumentIndex = currentDocumentsDataStateUpdated?.findIndex((customerData) => String(customerData._id) === String(selectedDocumentData._id)) ?? -1

                      if (findDocumentIndex !== -1 && currentDocumentsDataStateUpdated) {
                        currentDocumentsDataStateUpdated[findDocumentIndex].selected = false
                      }

                      currentDocumentsDataRef.current = currentDocumentsDataStateUpdated
                      setDocumentsData(currentDocumentsDataStateUpdated)
                    }

                    if (documentsToDeleteUpdated.length > 0) {
                      currentShowDeleteButtonFromActionsBarRef.current = true
                      setShowDeleteButtonFromActionsBar(true)
                    } else {
                      currentShowDeleteButtonFromActionsBarRef.current = false
                      setShowDeleteButtonFromActionsBar(false)
                    }

                    if (documentsList.length === documentsSelected) {
                      setSelectAllDocuments(true)
                    } else {
                      setSelectAllDocuments(false)
                    }

                    setDocumentsList(documentsListUpdated)
                  }}
                  handleOpenDeleteDocumentDataModal={(selectedDocumentData: Document) => {
                    const documentsToDeleteUpdated = [selectedDocumentData._id] as Array<string>
                    const currentDocumentsList = [...documentsList]
                    const documentsListUpdated = currentDocumentsList.map((documentData) => ({
                      ...documentData,
                      selected: false
                    }))

                    handleUncheckAllDocuments()
                    setSelectAllDocuments(false)
                    setOpenDeleteDocumentModal(true)
                    setDocumentsList(documentsListUpdated)
                    currentShowDeleteButtonFromActionsBarRef.current = false
                    setShowDeleteButtonFromActionsBar(false)
                    documentsToDeleteRef.current = documentsToDeleteUpdated
                    setDocumentsToDelete(documentsToDeleteUpdated)
                    handleChangeStateToOpenModal(selectedDocumentData)
                  }}
                />
              )
            })}
          </div>
          <Pagination
            pageSize={pageSize}
            currentPage={currentListPage}
            handlePaginationPageChange={(page: number) => {
              handleUncheckAllDocuments()

              setTimeout(() => {
                setScrollToTop(true)
                documentsToDeleteRef.current = []
                setDocumentsToDelete([])
                setSelectAllDocuments(false)
                currentListPageRef.current = page
                setCurrentListPage(page)
              }, 100)
            }}
            handleChangePageSize={(pageSizeToSet: string) => {
              setScrollToTop(true)
              setPageSize(Number(pageSizeToSet))
            }}
            totalCount={documentToSearch === '' ? documentsData.length : searchedDocuments.length}
          />
          {noRegistriesFoundAtLocal && (
            <div className="customer-no-data-found-container">
              {searchingInDatabase ? (
                <>
                  <div className="customer-no-data-found-searching-progress-bar">
                    <LinearProgress />
                  </div>
                  <div className="customer-no-data-found-no-registries-text">{t('searchingResultTexts.case3', { ns: 'customer-company-details-management' })}</div>
                </>
              ) : (
                <>
                  {noRegistriesFoundAtDatabase === false ? (
                    <>
                      <div className="customer-no-data-found-no-registries-text">
                        {t('searchingResultTexts.case1.firstPart', { ns: 'customer-company-details-management' })}{' '}
                        <strong
                          className="customer-no-data-found-no-registries-text-click"
                          onClick={() => handleSearchBatchesToReviewDataByRegex()}
                        >
                          {t('searchingResultTexts.case1.thirdPart', { ns: 'customer-company-details-management' })}
                        </strong>{' '}
                        {t('searchingResultTexts.case1.fourthPart', { ns: 'customer-company-details-management' })}
                      </div>
                      <Button
                        sx={baseButtonBlueBg}
                        onClick={handleSearchBatchesToReviewDataByRegex}
                      >
                        {t('searchingResultTexts.expandSearch', { ns: 'customer-company-details-management' })}
                      </Button>
                      {errors.generalError && (
                        <div
                          style={{ marginTop: '0px' }}
                          className="error-message"
                        >
                          {errors.generalError}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="customer-no-data-found-no-registries-text">
                        <img
                          src={HelpIcon}
                          alt="help-icon"
                        />
                      </div>
                      <div className="customer-no-data-found-no-registries-text">{t('searchingResultTexts.case2', { ns: 'customer-company-details-management' })}</div>
                    </>
                  )}
                </>
              )}
            </div>
          )}
          {openDeleteDocumentModal && (
            <Dialog
              errors={errors}
              actionButtonText={t('deleteDocumentModal.actionButtonText')}
              actionButtonWarningStyle
              dialogIsLoading={dialogDeleteDocumentIsLoading}
              handleDialogAction={() => handleConfirmDocumentDeletion()}
              dialogTitle={documentsToDelete.length === 1 ? t('deleteDocumentModal.title') : `${t('deleteDocumentModal.title')}s`}
              handleDialogOpenState={(openState: boolean) => {
                setOpenDeleteDocumentModal(openState)

                if (openState === false) {
                  setErrors({})
                  setSelectedDocument((currentSelectedDocumentState) => {
                    if (currentSelectedDocumentState.originalName) {
                      setDocumentsToDelete([])
                      documentsToDeleteRef.current = []
                    }

                    return {}
                  })
                }
              }}
            >
              <div className="dialog-content-text">
                {selectedDocument.originalName ? t('deleteDocumentModal.simpleDocumentToDeleteText', { documentOriginalName: selectedDocument.originalName }) : t('deleteDocumentModal.multipleDocumentToDeleteText')}
              </div>
            </Dialog>
          )}
          {openFiltersOptionsModal && (
            <FiltersOptionsModal
              filtersToApply="batches"
              systemUsersData={systemUsersData}
              handleDialogOpenState={(openState: boolean) => {
                setOpenFiltersOptionsModal(openState)
              }}
              title={t('filtersOptionsModal.documentsToReview.title', { ns: 'common' })}
              actionButtonText={t('filtersOptionsModal.documentsToReview.actionButtonText', { ns: 'common' })}
            />
          )}
          {openStopDocumentModal && (
            <Dialog
              errors={errors}
              actionButtonText={t('stopDocumentModal.actionButtonText')}
              actionButtonWarningStyle
              dialogIsLoading={dialogStopDocumentIsLoading}
              handleDialogAction={() => handleConfirmDocumentStop()}
              dialogTitle={t('stopDocumentModal.title')}
              handleDialogOpenState={(openState: boolean) => {
                setOpenStopDocumentModal(openState)

                if (openState === false) {
                  setErrors({})
                  setSelectedDocument({})
                }
              }}
            >
              <div className="dialog-content-text">{t('stopDocumentModal.warningText', { documentOriginalName: selectedDocument.originalName })}</div>
            </Dialog>
          )}
          {openResumeDocumentModal && (
            <Dialog
              errors={errors}
              actionButtonText={t('resumeDocumentModal.title')}
              actionButtonWarningStyle
              dialogIsLoading={dialogResumeDocumentIsLoading}
              handleDialogAction={() => handleConfirmDocumentResume()}
              dialogTitle={t('resumeDocumentModal.title')}
              handleDialogOpenState={(openState: boolean) => {
                setOpenResumeDocumentModal(openState)

                if (openState === false) {
                  setErrors({})
                  setSelectedDocument({})
                }
              }}
            >
              <div className="dialog-content-text">{t('resumeDocumentModal.warningText', { documentOriginalName: selectedDocument.originalName })}</div>
            </Dialog>
          )}
        </>
      )}
    </div>
  )
}

export default DocumentsToReviewManagement
